/**
 * Legacy Loader Bridge
 *
 * Safe wrapper around the AngularJS global loader.
 * Allows React components to show/hide the existing Angular loading spinner.
 *
 * This is a bridge utility for the hybrid AngularJS + React Shell application.
 * The loader UI and behavior remain owned by Angular. React triggers it via this API.
 *
 * Usage:
 *   import { showLegacyLoader, hideLegacyLoader } from '@/utils/legacyLoader';
 *
 *   showLegacyLoader();
 *   // ... do work
 *   hideLegacyLoader();
 *
 * Future Migration:
 *   When migrating to React-only, replace window.LegacyLoader calls
 *   with a pure React loader implementation.
 */

// TypeScript interface for the global Angular loader
declare global {
  interface Window {
    LegacyLoader?: {
      show: () => void;
      hide: () => void;
      _safeApply: (fn: () => void) => void;
    };
  }
}

/**
 * Check if the legacy Angular loader is available
 */
function isLegacyLoaderAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.LegacyLoader !== 'undefined' &&
    typeof window.LegacyLoader.show === 'function' &&
    typeof window.LegacyLoader.hide === 'function'
  );
}

// Old fallback overlay id — removed on hide() in case a prior build rendered it
const FALLBACK_ID = 'react-loading-overlay';

function hideFallbackLoader(): void {
  document.getElementById(FALLBACK_ID)?.remove();
}

/**
 * Show the legacy AngularJS loader.
 * In embedded mode, calls AngularJS LegacyLoader.show().
 * In standalone mode, no-op — AppLayout's <StandaloneSpinner> renders the
 * user-customizable GIF based on useUIStore.isLoading. Do NOT create a
 * generic CSS fallback spinner here, it would double-render.
 */
export function showLegacyLoader(): void {
  if (isLegacyLoaderAvailable()) {
    try {
      window.LegacyLoader!.show();
    } catch (error) {
      console.warn('[LegacyLoader] Failed to show loader:', error);
    }
  }
}

/**
 * Hide the legacy AngularJS loader.
 * Also cleans up any lingering fallback overlay left over from older builds.
 */
export function hideLegacyLoader(): void {
  if (isLegacyLoaderAvailable()) {
    try {
      window.LegacyLoader!.hide();
    } catch (error) {
      console.warn('[LegacyLoader] Failed to hide loader:', error);
    }
  }
  // Always try to remove any old fallback overlay (cheap no-op if not present)
  hideFallbackLoader();
}
